import { useCallback, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { getNinjaApplicantAccountRequests } from "@/lib/api/bankService";
import { showApiError } from "@/lib/utils/notifications";
import { BANK_LINKING_LOGS_VIEWER_ROLES } from "../../../../constants";
import type { BankLinkingRequestGroup } from "../../../../types/utils";
import {
  buildBankLinkingRequestGroups,
  hasAnyBankLinkingEventRows,
} from "../../../../utils/bankLinkingLogsMapper";

export function useBankLinkingLogs(options: {
  applicantId: string;
  hasBankLinkingSteps?: boolean;
}) {
  const { applicantId, hasBankLinkingSteps = false } = options;
  const { roleName } = useUserRole();

  const canViewLogs = BANK_LINKING_LOGS_VIEWER_ROLES.includes(roleName);
  const showViewLogsButton = canViewLogs && Boolean(hasBankLinkingSteps);

  const [logsLoading, setLogsLoading] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logRequests, setLogRequests] = useState<BankLinkingRequestGroup[]>([]);
  const [emptyLogsDialogOpen, setEmptyLogsDialogOpen] = useState(false);

  const openBankLinkingLogs = useCallback(async () => {
    if (!canViewLogs || !hasBankLinkingSteps) return;
    if (!applicantId || logsLoading) return;

    setLogsLoading(true);
    try {
      const data = await getNinjaApplicantAccountRequests(applicantId);
      const groups = buildBankLinkingRequestGroups(data);
      if (!hasAnyBankLinkingEventRows(groups)) {
        setEmptyLogsDialogOpen(true);
        return;
      }
      setLogRequests(groups);
      setLogsModalOpen(true);
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setLogsLoading(false);
    }
  }, [applicantId, canViewLogs, hasBankLinkingSteps, logsLoading]);

  const closeLogsModal = useCallback(() => {
    setLogsModalOpen(false);
  }, []);

  return {
    showViewLogsButton,
    logsLoading,
    openBankLinkingLogs,
    logsModalOpen,
    closeLogsModal,
    logRequests,
    emptyLogsDialogOpen,
    setEmptyLogsDialogOpen,
  };
}
